import React, { useState, useRef, MouseEvent } from 'react';
import { Copy, Check, Sparkles, Share2, Box, Zap } from 'lucide-react';

interface CouponCardProps {
  code: string;
  title?: string;
  discount?: string;
  variant?: 'primary' | 'secondary' | 'accent';
}

export const CouponCard: React.FC<CouponCardProps> = ({ code, title = "Active Coupon Code", discount = "MAX DISCOUNT", variant = 'primary' }) => {
  const [copied, setCopied] = React.useState(false);
  const [shareCopied, setShareCopied] = React.useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    // Disable complex tilt on very small screens to improve performance and prevent layout jitter
    if (window.innerWidth < 640) return;
    
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePos({ x, y });

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -10; 
    const rotateY = ((x - centerX) / centerX) * 10;
    
    setTilt({ x: rotateX, y: rotateY });
  };

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => {
    setIsHovering(false);
    setTilt({ x: 0, y: 0 });
  };

  const handleShare = async () => {
    const shareData = {
      title: 'PW Coupon Code 2026',
      text: `Get ${discount} on PW Batches using code: ${code}. Check it out here:`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled or failed', err);
      }
    } else {
      navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'primary': return 'border-blue-500/30 bg-blue-900/10 shadow-[0_30px_60px_rgba(37,99,235,0.2)] sm:shadow-[0_45px_100px_rgba(37,99,235,0.25)]';
      case 'secondary': return 'border-green-500/30 bg-green-900/10 shadow-[0_30px_60px_rgba(34,197,94,0.2)] sm:shadow-[0_45px_100px_rgba(34,197,94,0.25)]';
      case 'accent': return 'border-yellow-500/30 bg-yellow-900/10 shadow-[0_30px_60px_rgba(234,179,8,0.2)] sm:shadow-[0_45px_100px_rgba(234,179,8,0.25)]';
      default: return 'border-white/10 bg-white/5';
    }
  };

  const getButtonStyles = () => {
    switch (variant) {
      case 'primary': return 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/40';
      case 'secondary': return 'bg-green-500 hover:bg-green-600 shadow-green-500/40';
      case 'accent': return 'bg-yellow-500 hover:bg-yellow-600 shadow-yellow-500/40 text-black';
      default: return 'bg-white text-black';
    }
  };

  const getTextColor = () => {
    switch (variant) {
      case 'primary': return 'text-blue-400';
      case 'secondary': return 'text-green-400';
      case 'accent': return 'text-yellow-400';
      default: return 'text-white';
    }
  };

  return (
    <div 
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1500px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateY(${isHovering && window.innerWidth >= 640 ? -12 : 0}px)`,
        transition: isHovering ? 'none' : 'transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.5s ease',
        transformStyle: 'preserve-3d'
      }}
      className={`group p-8 sm:p-12 rounded-[35px] sm:rounded-[60px] border-2 ${getVariantStyles()} transition-all card-3d relative overflow-hidden flex flex-col items-center text-center w-full max-w-[320px] sm:max-w-[440px]`}
    >
      <div 
        className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background: `radial-gradient(circle at ${mousePos.x}px ${mousePos.y}px, rgba(255,255,255,0.12), transparent 50%)`,
        }}
      />
      
      <div className="relative z-10 w-full flex flex-col items-center">
        <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6 [transform:translateZ(40px)]">
          <Sparkles className="w-4 h-4 text-yellow-400 animate-pulse" />
          <span className="text-[8px] sm:text-[11px] font-black tracking-[0.3em] uppercase text-gray-500">{title}</span>
        </div>
        
        <h3 className="text-3xl sm:text-6xl font-black mb-2 text-white italic uppercase tracking-tighter leading-none [transform:translateZ(110px)] drop-shadow-[0_15px_35px_rgba(0,0,0,0.6)]">
          {discount}
        </h3>
        
        <p className="text-[8px] sm:text-xs text-gray-500 mb-6 sm:mb-12 font-bold uppercase tracking-widest [transform:translateZ(55px)]">
          Verified 2026 Enrollment
        </p>
        
        <div className="w-full bg-black/70 rounded-[25px] sm:rounded-[40px] border border-white/10 p-5 sm:p-10 mb-6 sm:mb-12 relative overflow-hidden group/code shadow-[inset_0_2px_20px_rgba(0,0,0,0.4)] [transform:translateZ(85px)]">
          <div className="relative flex flex-col gap-4 sm:gap-4">
            <span className={`text-2xl sm:text-4xl font-mono font-black tracking-tighter ${getTextColor()} drop-shadow-[0_0_15px_rgba(0,0,0,0.5)]`}>
              {code}
            </span>
            <div className="flex gap-2 sm:gap-3 w-full">
              <button 
                onClick={handleShare}
                className="flex-1 p-4 sm:p-5 rounded-2xl sm:rounded-3xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all active:scale-90 border border-white/5 flex items-center justify-center"
              >
                {shareCopied ? <Check size={18} className="text-green-500" /> : <Share2 size={18} />}
              </button>
              <button 
                onClick={handleCopy}
                className={`flex-[3] p-4 sm:p-5 rounded-2xl sm:rounded-3xl transition-all active:scale-95 shadow-2xl ${getButtonStyles()} text-white flex items-center justify-center gap-2`}
              >
                {copied ? <Check size={20} /> : <Copy size={20} />}
                <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest">{copied ? 'COPIED' : 'COPY'}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 [transform:translateZ(35px)] opacity-60">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
          <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-gray-400">Ambassador Active</span>
        </div>
      </div>
    </div>
  );
};